/**
 * Renders the Home doc-list: a sticky header, a virtualized row window and the
 * statistics footer. Consumed by `HomeFormVersionsTable`, which owns the store.
 *
 * `store.queryDisabledReason` outranks `data`: while it is set the query was never
 * sent, so whatever `data` still holds is stale or empty and no row may be drawn.
 * The reason takes the rows' place and stays until the filters fit again, which is
 * what keeps a refused query from reading as an answer. The header keeps rendering,
 * so the filters that caused it remain editable.
 *
 * Card mode (SYD-3046): below `md`, when a smaller breakpoint is positively reported,
 * each row becomes a card — the link column as its title line, then every other
 * column as a label beside its value over the `CARD_GRID_TEMPLATE` tracks. Every
 * line is one `controlHeight`, so a card's slot height stays exact and the row
 * window keeps virtualizing. The actions column is dropped, nothing is pinned left,
 * and the header becomes a strip of column chips.
 */
import { Fragment, useEffect, useImperativeHandle, useMemo, useRef, useState, useCallback, useContext } from 'react';
import { useSnapshot } from 'valtio';
import { Grid, Spin, Typography, theme } from 'antd';
import { Link } from 'react-router-dom';
import type { GlobalToken } from 'antd';
import type { FieldType, FieldId } from '#types/System.types';
import { UIElementType } from '#types/System.types';
import type { DocListTableProps } from './DocListTable.types';
import type { BaseListDocument, ColumnSpec } from '../DocList.types';
import { DocListStoreContext } from '../createDocListTableStore';
import { FieldTypeContext } from '@/providers/FieldTypeContext';
import ControlUI from '@/components/rendering/ControlUI';

import { ACTIONS_COLUMN_ID } from './constants';
import DocListTableLayout from './components/DocListTableLayout';
import DocListTableHeader from './components/DocListTableHeader';
import DocListTableCell from './components/DocListTableCell';
import DocListTableFooter, { type ComputedStatistic } from './components/DocListTableFooter';
import ColumnHeaderContextMenu from './components/ColumnHeaderContextMenu';
import ColumnPicker from './components/ColumnPicker';

const { Text } = Typography;

const CARD_GRID_TEMPLATE = 'fit-content(40%) minmax(0, 1fr)';

function renderCellValue<TDoc>(
  column: ColumnSpec<TDoc>,
  row: TDoc,
  rowId: string,
  getLabel: ((columnId: string, value: unknown) => string) | undefined,
  fieldTypes: Record<string, FieldType>,
  token: GlobalToken,
): React.ReactNode {
  const value = column.accessor(row);

  if (column.link) {
    const path = column.link.getPath(row);
    const text = column.link.getDisplayText
      ? column.link.getDisplayText(row)
      : String(value ?? '');
    return (
      <Link
        to={path}
        style={{
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          margin: 0,
          padding: 0,
          color: token.colorPrimary,
          textDecoration: 'none',
        }}
        title={text}
      >
        {text}
      </Link>
    );
  }

  if (column.render) {
    return column.render(value, row);
  }

  if (column.id.startsWith('field_')) {
    const fieldId = column.id.slice('field_'.length);
    const fieldType = fieldTypes?.[fieldId];
    if (fieldType) {
      return (
        <ControlUI
          id={`doclist-cell-${column.id}-${rowId}`}
          value={value}
          fieldType={fieldType}
          uiField={{
            uiElementType: UIElementType.Field,
            fieldId: fieldId as FieldId,
            props: { variant: 'borderless' },
          }}
          onChange={() => undefined}
          readOnly
          tableMode
          {...fieldType?.props}
        />
      );
    }
  }

  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (Array.isArray(value)) {
    const parts = value
      .filter((v) => v !== null && v !== undefined && String(v) !== '')
      .map((v) => (getLabel ? getLabel(column.id, v) || String(v) : String(v)));
    return <Text ellipsis>{parts.join(', ')}</Text>;
  }

  const labeled = getLabel ? getLabel(column.id, value) : '';
  return <Text ellipsis>{labeled || String(value)}</Text>;
}

function DocListTable<TDoc extends BaseListDocument>({
  ref,
  store,
  data,
  config,
  visibleColumns,
  gridTemplateColumns,
  frozenLeftOffsets,
  rowHeight = 32,
  hasMore,
  isLoadingMore,
  loadMore,
  getLabel,
  columnStatistics,
  setColumnStatistic,
  handleRemoveColumn,
  requiredColumnIds = [],
  enableFacetedValues = false,
}: DocListTableProps<TDoc>) {
  const { token } = theme.useToken();
  const { xs, sm, md } = Grid.useBreakpoint();
  const cardMode = !md && Boolean(xs || sm);
  const { fieldTypes } = useContext(FieldTypeContext);
  const { queryDisabledReason } = useSnapshot(store);
  const scrollRef = useRef<HTMLDivElement>(null);

  const [contextMenu, setContextMenu] = useState<{
    columnId: string;
    position: { x: number; y: number };
  } | null>(null);

  const [insertPicker, setInsertPicker] = useState<{
    anchorColumnId: string;
    position: { x: number; y: number };
    title: string;
  } | null>(null);

  const openContextMenu = useCallback((columnId: string, position: { x: number; y: number }) => {
    setContextMenu({ columnId, position });
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const openInsertPicker = useCallback((anchorColumnId: string, position: { x: number; y: number }) => {
    setInsertPicker({ anchorColumnId, position, title: 'Insert column (left)' });
  }, []);

  const closeInsertPicker = useCallback(() => {
    setInsertPicker(null);
  }, []);

  const openAddColumnPicker = useCallback(() => {
    const rect = scrollRef.current?.getBoundingClientRect();
    setInsertPicker({
      anchorColumnId: '',
      position: { x: rect?.left ?? 0, y: (rect?.top ?? 0) + rowHeight },
      title: 'Add column',
    });
  }, [rowHeight]);

  useImperativeHandle(ref, () => ({ openAddColumnPicker }), [openAddColumnPicker]);

  const getColumnValueType = useCallback((columnId: string): 'string' | 'number' | 'date' | 'array' => {
    const column = config.columns.find((c: ColumnSpec<TDoc>) => c.id === columnId);
    if (column?.valueType) {
      return column.valueType;
    }

    for (const row of data) {
      const value = column?.accessor(row);
      if (value !== null && value !== undefined && value !== '') {
        if (typeof value === 'number') return 'number';
        if (value instanceof Date || (typeof value === 'object' && '_seconds' in value)) return 'date';
        if (Array.isArray(value)) return 'array';
        return 'string';
      }
    }
    return 'string';
  }, [config.columns, data]);

  const getColumnHeader = useCallback((columnId: string): string => {
    const column = config.columns.find((c: ColumnSpec<TDoc>) => c.id === columnId);
    return column?.header ?? columnId;
  }, [config.columns]);

  const computedStatistics = useMemo(() => {
    const stats: Record<string, ComputedStatistic> = {};

    Object.entries(columnStatistics).forEach(([columnId, statType]) => {
      const column = config.columns.find((c: ColumnSpec<TDoc>) => c.id === columnId);
      if (!column) return;

      const values = data.map(row => column.accessor(row));

      let value = 0;
      let formattedValue = '';

      switch (statType) {
        case 'count':
          value = values.filter(v =>
            v !== null && v !== undefined && v !== ''
          ).length;
          formattedValue = `Count: ${value.toLocaleString()}`;
          break;

        case 'sum': {
          value = values.reduce((acc: number, v) => {
            const num: number = typeof v === 'number' ? v : parseFloat(String(v));
            return acc + (isNaN(num) ? 0 : num);
          }, 0);

          if (column.formatType === 'currency') {
            const hasDecimals = value % 1 !== 0;
            const formattedNum = hasDecimals ? value.toFixed(2) : value.toString();
            const withCommas = formattedNum.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            formattedValue = `Sum: $${withCommas}`;
          } else if (column.formatType === 'percentage') {
            const hasDecimals = value % 1 !== 0;
            const formattedNum = hasDecimals ? value.toFixed(1) : value.toString();
            formattedValue = `Sum: ${formattedNum}%`;
          } else {
            formattedValue = `Sum: ${value.toLocaleString(undefined, {
              maximumFractionDigits: 2
            })}`;
          }
          break;
        }

        case 'avg': {
          const numericValues = values
            .map(v => typeof v === 'number' ? v : parseFloat(String(v)))
            .filter(n => !isNaN(n));

          if (numericValues.length > 0) {
            const sum = numericValues.reduce((a, b) => a + b, 0);
            value = sum / numericValues.length;

            if (column.formatType === 'currency') {
              const hasDecimals = value % 1 !== 0;
              const formattedNum = hasDecimals ? value.toFixed(2) : value.toString();
              const withCommas = formattedNum.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
              formattedValue = `Avg: $${withCommas}`;
            } else if (column.formatType === 'percentage') {
              const hasDecimals = value % 1 !== 0;
              const formattedNum = hasDecimals ? value.toFixed(1) : value.toString();
              formattedValue = `Avg: ${formattedNum}%`;
            } else {
              formattedValue = `Avg: ${value.toLocaleString(undefined, {
                maximumFractionDigits: 2
              })}`;
            }
          } else {
            formattedValue = 'Avg: -';
          }
          break;
        }
      }

      stats[columnId] = { type: statType, value, formattedValue };
    });

    return stats;
  }, [data, columnStatistics, config.columns]);

  const hasAnyStatistics = Object.keys(computedStatistics).length > 0;
  const statisticsFooterHeight = hasAnyStatistics ? 32 : 0;
  const [scrollTop, setScrollTop] = useState<number>(0);
  const [viewportHeight, setViewportHeight] = useState<number>(0);
  const lastScrollTopRef = useRef<number>(0);
  const hasMoreRef = useRef<boolean>(hasMore);
  const isLoadingMoreRef = useRef<boolean>(isLoadingMore);
  const loadMoreRef = useRef<() => Promise<void>>(loadMore);

  useEffect(() => {
    hasMoreRef.current = hasMore;
    isLoadingMoreRef.current = isLoadingMore;
    loadMoreRef.current = loadMore;
  }, [hasMore, isLoadingMore, loadMore]);

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return;

    const handleScroll = () => {
      const { scrollTop: nextScrollTop, scrollHeight, clientHeight } = scrollElement;

      if (nextScrollTop === lastScrollTopRef.current) {
        return;
      }

      lastScrollTopRef.current = nextScrollTop;
      setScrollTop(nextScrollTop);
      setViewportHeight(clientHeight);

      if (!hasMoreRef.current || isLoadingMoreRef.current) {
        return;
      }

      const scrollPercentage = (nextScrollTop + clientHeight) / scrollHeight;

      if (scrollPercentage > 0.8) {
        void loadMoreRef.current();
      }
    };

    lastScrollTopRef.current = scrollElement.scrollTop;
    setScrollTop(scrollElement.scrollTop);
    setViewportHeight(scrollElement.clientHeight);

    const resizeObserver = new ResizeObserver(() => {
      setViewportHeight(scrollElement.clientHeight);
    });
    resizeObserver.observe(scrollElement);

    scrollElement.addEventListener('scroll', handleScroll);
    return () => {
      scrollElement.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
    };
  }, []);

  const cardColumns = visibleColumns.filter((c) => c.id !== ACTIONS_COLUMN_ID);
  const titleColumn = cardColumns.find((c) => c.link) ?? cardColumns[0];
  const bodyColumns = cardColumns.filter((c) => c !== titleColumn);
  const slotHeight = cardMode
    ? cardColumns.length * token.controlHeight + token.paddingXS * 2 + token.lineWidth * 2 + token.marginXS
    : rowHeight;

  const rowCount = queryDisabledReason ? 0 : data.length;
  const totalSize = rowCount * slotHeight;
  const overscan = 10;
  const startIndex = Math.max(0, Math.floor(scrollTop / slotHeight) - overscan);
  const endIndex = Math.min(
    rowCount,
    Math.ceil((scrollTop + viewportHeight) / slotHeight) + overscan
  );

  const virtualRows = useMemo(
    () =>
      Array.from({ length: Math.max(0, endIndex - startIndex) }, (_, i) => {
        const index = startIndex + i;
        return {
          index,
          size: slotHeight,
          start: index * slotHeight,
        };
      }),
    [endIndex, slotHeight, startIndex]
  );

  const visibleColumnIds = visibleColumns.map(c => c.id);
  const visibleColumnIdsKey = visibleColumnIds.join('|');

  const hiddenColumnOptions = useMemo(() => {
    const visible = new Set(
      visibleColumnIdsKey
        .split('|')
        .map((id) => id.trim())
        .filter(Boolean)
    );

    return config.columns
      .filter((col) => !visible.has(col.id))
      .map((col) => ({
        id: col.id,
        label: col.header ?? col.id,
      }));
  }, [config.columns, visibleColumnIdsKey]);

  const insertColumnLeftOf = useCallback(
    (anchorColumnId: string, columnIdToInsert: string) => {
      const enableActionsColumn = config.enableActionsColumn !== false;
      const currentVisible = visibleColumnIds.filter((id) => id !== ACTIONS_COLUMN_ID);

      if (currentVisible.includes(columnIdToInsert)) return;

      const anchorIndex = currentVisible.findIndex((id) => id === anchorColumnId);
      const index = anchorIndex >= 0 ? anchorIndex : currentVisible.length;
      const nextVisible = [
        ...currentVisible.slice(0, index),
        columnIdToInsert,
        ...currentVisible.slice(index),
      ];

      store.columnOrder = enableActionsColumn ? [...nextVisible, ACTIONS_COLUMN_ID] : nextVisible;

      const allColumnIds = config.columns.map((c) => c.id);
      const visibility: Record<string, boolean> = {};
      for (const id of allColumnIds) {
        visibility[id] = id === ACTIONS_COLUMN_ID || nextVisible.includes(id);
      }
      store.columnVisibility = visibility;
    },
    [config.enableActionsColumn, config.columns, visibleColumnIds, store]
  );

  const footerColumns = visibleColumns.map(col => ({ id: col.id, header: col.header }));

  return (
    <DocListStoreContext.Provider value={store}>
    <DocListTableLayout scrollRef={scrollRef} cardMode={cardMode}>
      <DocListTableHeader
        gridTemplateColumns={gridTemplateColumns}
        visibleColumns={cardMode ? cardColumns : visibleColumns}
        data={data}
        frozenLeftOffsets={frozenLeftOffsets}
        getLabel={getLabel}
        onOpenContextMenu={openContextMenu}
        enableFacetedValues={enableFacetedValues}
        cardMode={cardMode}
      />

      <div
        style={{
          height: `${totalSize + statisticsFooterHeight}px`,
          width: '100%',
          position: 'relative',
          flex: hasAnyStatistics ? undefined : 1,
          paddingBottom: statisticsFooterHeight,
        }}
      >
        {virtualRows.map((virtualRow) => {
          const row = data[virtualRow.index];

          if (!row) {
            return null;
          }

          const rowId = row.docId ?? String(virtualRow.index);

          if (cardMode) {
            return (
              <div
                key={rowId}
                aria-label={`doclist-table-row-${rowId}`}
                role="row"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: token.marginXS,
                  right: token.marginXS,
                  height: `${virtualRow.size - token.marginXS}px`,
                  transform: `translateY(${virtualRow.start + token.marginXS}px)`,
                  display: 'grid',
                  gridTemplateColumns: CARD_GRID_TEMPLATE,
                  gridAutoRows: token.controlHeight,
                  alignItems: 'center',
                  columnGap: token.paddingXS,
                  paddingBlock: token.paddingXS,
                  borderStyle: 'solid',
                  borderColor: token.colorBorderSecondary,
                  borderWidth: token.lineWidth,
                  borderRadius: token.borderRadiusLG,
                  background: token.colorBgContainer,
                  boxSizing: 'border-box',
                  overflow: 'hidden',
                }}
              >
                {titleColumn && (
                  <DocListTableCell spanAll cardMode>
                    {renderCellValue(titleColumn, row, rowId, getLabel, fieldTypes, token)}
                  </DocListTableCell>
                )}
                {bodyColumns.map((col) => (
                  <Fragment key={col.id}>
                    <Text
                      type="secondary"
                      title={col.header}
                      style={{
                        gridColumn: 1,
                        paddingInlineStart: token.paddingSM,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                      }}
                    >
                      {col.header}
                    </Text>
                    <DocListTableCell cardMode>
                      {renderCellValue(col, row, rowId, getLabel, fieldTypes, token)}
                    </DocListTableCell>
                  </Fragment>
                ))}
              </div>
            );
          }

          return (
            <div
              key={rowId}
              aria-label={`doclist-table-row-${rowId}`}
              role="row"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start}px)`,
                display: 'grid',
                gridTemplateColumns,
                alignItems: 'center',
                borderStyle: 'solid',
                borderColor: token.colorBorderSecondary,
                borderWidth: '0 0 1px 0',
              }}
            >
              {visibleColumns.map((col) => (
                <DocListTableCell
                  key={col.id}
                  frozenLeftOffset={frozenLeftOffsets.get(col.id)}
                >
                  {renderCellValue(col, row, rowId, getLabel, fieldTypes, token)}
                </DocListTableCell>
              ))}
            </div>
          );
        })}

        {queryDisabledReason ? (
          <div
            role="status"
            aria-label="doclist-query-disabled-reason"
            style={{
              padding: token.paddingXL * 2,
              textAlign: 'center',
            }}
          >
            <Text type="warning">{queryDisabledReason}</Text>
          </div>
        ) : data.length === 0 && (
          <div
            style={{
              padding: token.paddingXL * 2,
              textAlign: 'center',
              color: token.colorTextTertiary,
            }}
          >
            No documents found
          </div>
        )}

        {isLoadingMore && (
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: 60,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: token.colorBgContainer,
            }}
          >
            <Spin size="small" />
            <Text style={{ marginLeft: token.marginSM }}>Loading more...</Text>
          </div>
        )}
      </div>

      <DocListTableFooter
        gridTemplateColumns={gridTemplateColumns}
        visibleColumns={footerColumns}
        statistics={computedStatistics}
        frozenLeftOffsets={frozenLeftOffsets}
        cardMode={cardMode}
      />

      {contextMenu && (
        <ColumnHeaderContextMenu
          columnId={contextMenu.columnId}
          columnHeader={getColumnHeader(contextMenu.columnId)}
          valueType={getColumnValueType(contextMenu.columnId)}
          position={contextMenu.position}
          isRequired={requiredColumnIds.includes(contextMenu.columnId)}
          currentStatistic={columnStatistics[contextMenu.columnId] ?? null}
          onClose={closeContextMenu}
          onRemoveColumn={handleRemoveColumn}
          onSetStatistic={setColumnStatistic}
          onRequestInsertColumn={openInsertPicker}
        />
      )}

      {insertPicker && (
        <ColumnPicker
          open={true}
          position={insertPicker.position}
          options={hiddenColumnOptions}
          title={insertPicker.title}
          cardMode={cardMode}
          onPick={(columnId) => {
            insertColumnLeftOf(insertPicker.anchorColumnId, columnId);
          }}
          onClose={closeInsertPicker}
        />
      )}
    </DocListTableLayout>
    </DocListStoreContext.Provider>
  );
}

export default DocListTable;
